import { type ElementType, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

type AnimatedButtonVariant = 'primary' | 'secondary';

interface AnimatedButtonProps {
    component?: ElementType;
    children: ReactNode;
    icon?: ReactNode;
    hoverIcon?: ReactNode;
    variant?: AnimatedButtonVariant;
    transparent?: boolean;
    className?: string;
    faceClassName?: string;
    type?: 'button' | 'submit' | 'reset';
    [key: string]: unknown;
}

export default function AnimatedButton({
    component: Component = 'button',
    children,
    icon,
    hoverIcon,
    variant = 'primary',
    transparent = false,
    className,
    faceClassName,
    type,
    ...props
}: AnimatedButtonProps) {
    const isButton = Component === 'button';

    const variantClasses = {
        primary: 'border-black bg-white text-black',
        secondary: 'border-black bg-black text-white',
    }[variant];

    const transparentClasses = {
        primary: 'bg-transparent text-black',
        secondary: 'bg-transparent text-white border-white',
    }[variant];

    const faceTwoClasses = {
        primary: 'bg-black text-white',
        secondary: 'bg-white text-black',
    }[variant];

    return (
        <Component
            type={isButton ? type ?? 'button' : type}
            className={cn(
                'group/animated-button relative inline-flex min-h-10 items-center justify-center overflow-hidden rounded-full border px-5 py-2 font-lato text-sm transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'disabled:pointer-events-none disabled:opacity-50',
                transparent ? transparentClasses : variantClasses,
                className
            )}
            {...props}
        >
            {/* Invisible sizing content */}
            <span
                className={cn(
                    'invisible flex items-center justify-center gap-2',
                    faceClassName
                )}
                aria-hidden="true"
            >
                {icon}
                {children}
            </span>

            {/* Button viewport */}
            <span className="absolute inset-0 overflow-hidden">
                <span className="flex h-[200%] w-full flex-col transition-transform duration-700 ease-out group-hover/animated-button:-translate-y-1/2">
                    {/* Face 1 */}
                    <span
                        className={cn(
                            'flex w-full shrink-0 items-center justify-center gap-2',
                            faceClassName,
                            'h-1/2'
                        )}
                    >
                        {icon}
                        {children}
                    </span>

                    {/* Face 2 */}
                    <span
                        className={cn(
                            'flex w-full shrink-0 items-center justify-center gap-2',
                            faceTwoClasses,
                            faceClassName,
                            'h-1/2'
                        )}
                        aria-hidden="true"
                    >
                        {hoverIcon ?? icon}
                        {children}
                    </span>
                </span>
            </span>
        </Component>
    );
}